import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { Link } from "react-router-dom";
import {
  MdPeople,
  MdPersonAdd,
  MdCheckCircle,
  MdCancel,
  MdBeachAccess,
  MdPayments,
  MdBusiness,
  MdAccessTime,
  MdArrowForward,
} from "react-icons/md";

// TODO: Replace with your actual API service
// import {
//   getHRMSDashboard,
//   getTodayHRMSDashboard,
// } from "../../../redux/services/hrmsDashboardServices/hrmsDashboard.Services";

const HRMSDashboard = () => {
  const [view, setView] = useState("overall");
  const [loading, setLoading] = useState(true);

  const [summary, setSummary] = useState({});
  const [todaySummary, setTodaySummary] = useState({});

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        setLoading(true);

        // TODO: API integration
        // const [overall, today] = await Promise.all([
        //   getHRMSDashboard(),
        //   getTodayHRMSDashboard(),
        // ]);
        //
        // setSummary(overall?.data || {});
        // setTodaySummary(today?.data || {});

        setSummary({});
        setTodaySummary({});
      } catch (error) {
        console.error("HRMS Dashboard Error:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  const currentData = view === "overall" ? summary : todaySummary;

  const overallTiles = useMemo(
    () => [
      {
        icon: <MdPeople />,
        label: "Total Employees",
        href: "/employee-list",
        points: summary?.total_employees || 0,
        accent: "cyan",
        helper: "All employees",
      },
      {
        icon: <MdCheckCircle />,
        label: "Present Today",
        href: "/attendance-list",
        points: summary?.present_today || 0,
        accent: "green",
        helper: "Present employees",
      },
      {
        icon: <MdCancel />,
        label: "Absent Today",
        href: "/attendance-list",
        points: summary?.absent_today || 0,
        accent: "red",
        helper: "Absent employees",
      },
      {
        icon: <MdBeachAccess />,
        label: "Leave Requests",
        href: "/leave-list",
        points: summary?.leave_requests || 0,
        accent: "orange",
        helper: "Pending leaves",
      },
      {
        icon: <MdPayments />,
        label: "Payroll",
        href: "/payroll-list",
        points: summary?.payroll_count || 0,
        accent: "purple",
        helper: "Payroll records",
      },
      {
        icon: <MdBusiness />,
        label: "Departments",
        href: "/department-list",
        points: summary?.total_departments || 0,
        accent: "blue",
        helper: "Active departments",
      },
    ],
    [summary]
  );

  const todayTiles = useMemo(
    () => [
      {
        icon: <MdPeople />,
        label: "Today's Employees",
        href: "/employee-list",
        points: todaySummary?.total_employees || 0,
        accent: "cyan",
        helper: "Employee activity",
      },
      {
        icon: <MdCheckCircle />,
        label: "Present Today",
        href: "/attendance-list",
        points: todaySummary?.present_today || 0,
        accent: "green",
        helper: "Present employees",
      },
      {
        icon: <MdCancel />,
        label: "Absent Today",
        href: "/attendance-list",
        points: todaySummary?.absent_today || 0,
        accent: "red",
        helper: "Absent employees",
      },
      {
        icon: <MdBeachAccess />,
        label: "Today's Leave",
        href: "/leave-list",
        points: todaySummary?.leave_today || 0,
        accent: "orange",
        helper: "Leave activity",
      },
      {
        icon: <MdAccessTime />,
        label: "Late Employees",
        href: "/attendance-list",
        points: todaySummary?.late_employees || 0,
        accent: "yellow",
        helper: "Late attendance",
      },
    ],
    [todaySummary]
  );

  const tiles = view === "overall" ? overallTiles : todayTiles;

  const attendanceChart = {
    series: [
      Number(currentData?.present_today || 0),
      Number(currentData?.absent_today || 0),
      Number(currentData?.leave_today || 0),
    ],
    options: {
      chart: {
        type: "donut",
        toolbar: { show: false },
      },
      labels: ["Present", "Absent", "On Leave"],
      legend: {
        position: "bottom",
      },
      dataLabels: {
        enabled: true,
      },
    },
  };

  const attendancePerformance = currentData?.attendance_performance || [];

  const attendanceBar = {
    series: [
      {
        name: "Present",
        data: attendancePerformance.map((item) =>
          Number(item?.present || 0)
        ),
      },
      {
        name: "Absent",
        data: attendancePerformance.map((item) =>
          Number(item?.absent || 0)
        ),
      },
    ],
    options: {
      chart: {
        type: "bar",
        toolbar: { show: false },
      },
      xaxis: {
        categories: attendancePerformance.map(
          (item) => item?.month || item?.date || ""
        ),
      },
      plotOptions: {
        bar: {
          borderRadius: 5,
          columnWidth: "45%",
        },
      },
      legend: {
        position: "bottom",
      },
    },
  };

  if (loading) {
    return (
      <div className="crm-dashboard crm-dashboard-loading">
        <div className="crm-loader-ring"></div>
      </div>
    );
  }

  return (
    <div className="crm-dashboard">
      <section className="crm-dashboard-hero">
        <div className="crm-hero-glow"></div>

        <div className="crm-hero-content">
          <div>
            <span className="crm-ai-badge">
              <MdPeople /> HRMS
            </span>

            <h1>HRMS Management Dashboard</h1>

            <p>
              Manage employees, attendance, leaves, payroll and workforce
              operations from one place.
            </p>

            <div className="crm-hero-meta">
              <span>
                <MdAccessTime />
                Workforce overview
              </span>
            </div>
          </div>

          <div className="crm-hero-stats">
            <div>
              <strong>{currentData?.total_employees || 0}</strong>
              <span>Total Employees</span>
            </div>

            <div>
              <strong>{currentData?.present_today || 0}</strong>
              <span>Present Today</span>
            </div>
          </div>
        </div>
      </section>

      <div className="crm-section-head">
        <div>
          <span className="crm-eyebrow">HRMS OVERVIEW</span>
          <h2>Workforce Analytics</h2>
        </div>

        <div className="crm-view-switch">
          <button
            className={view === "overall" ? "active" : ""}
            onClick={() => setView("overall")}
          >
            Overall
          </button>

          <button
            className={view === "today" ? "active" : ""}
            onClick={() => setView("today")}
          >
            Today
          </button>
        </div>
      </div>

      <div className="crm-kpi-grid">
        {tiles.map((item, index) => (
          <Link
            to={item.href}
            className={`crm-kpi-card ${item.accent}`}
            key={index}
          >
            <div className="crm-kpi-top">
              <div className="crm-kpi-icon">{item.icon}</div>
              <MdArrowForward className="crm-kpi-arrow" />
            </div>

            <div className="crm-kpi-value">{item.points}</div>
            <div className="crm-kpi-label">{item.label}</div>
            <div className="crm-kpi-helper">{item.helper}</div>
            <div className="crm-kpi-line"></div>
          </Link>
        ))}
      </div>

      <div className="crm-chart-grid">
        <div className="crm-chart-card">
          <div className="crm-card-heading">
            <div>
              <span className="crm-live-dot"></span>
              Attendance Status
            </div>
            <span className="crm-period-chip">Today</span>
          </div>

          <div className="crm-chart-body">
            <Chart
              options={attendanceChart.options}
              series={attendanceChart.series}
              type="donut"
              height={320}
            />
          </div>
        </div>

        <div className="crm-chart-card">
          <div className="crm-card-heading">
            <div>
              <span className="crm-live-dot"></span>
              Attendance Performance
            </div>
            <span className="crm-period-chip">Monthly</span>
          </div>

          <div className="crm-chart-body">
            {attendancePerformance.length ? (
              <Chart
                options={attendanceBar.options}
                series={attendanceBar.series}
                type="bar"
                height={320}
              />
            ) : (
              <div className="crm-empty-chart">
                No attendance data available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRMSDashboard;